import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Car, Megaphone, Send, CheckCircle, AlertCircle } from 'lucide-react';

const LOGO_URL = '/advecti-logo.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-cream-light" data-testid="contact-page">
      {/* Hero */}
      <section className="hero-gradient py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-navy/70 max-w-2xl mx-auto">
            Ready to get started? Have questions? Fill out our form and we'll 
            help you with everything you need.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 bg-white" data-testid="contact-main">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div>
              <img 
                src={LOGO_URL} 
                alt="Advecti Logo" 
                className="h-24 w-auto mb-8"
              />
              <h2 className="text-2xl font-bold text-navy mb-6">Send Us a Message</h2>
              
              {status === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-green-700 mb-4">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setStatus(null)}
                    className="bg-navy text-cream px-6 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-navy font-medium mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-navy font-medium mb-2">Your Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-navy font-medium mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors"
                      placeholder="How can we help?"
                      data-testid="contact-subject-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-navy font-medium mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-navy/20 rounded-xl focus:border-navy focus:outline-none transition-colors resize-none"
                      placeholder="Tell us what you're interested in..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl">
                      <AlertCircle size={20} />
                      <span>Failed to send message. Please try again.</span>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-bold transition-all ${
                      isSubmitting
                        ? 'bg-navy/50 text-cream/70 cursor-not-allowed'
                        : 'bg-navy text-cream hover:bg-navy-dark'
                    }`}
                    data-testid="contact-submit-btn"
                  >
                    <Send size={20} />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}

              {/* Location & Response Time */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-4 text-navy/70">
                  <MapPin size={20} />
                  <span>Columbus Metropolitan Area, Ohio</span>
                </div>
                <div className="flex items-center gap-4 text-navy/70">
                  <Clock size={20} />
                  <span>We respond within 24 hours</span>
                </div>
              </div>
            </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section className="py-16 bg-cream" data-testid="location-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-navy mb-4">Serving Columbus, Ohio</h2>
          <p className="text-xl text-navy/70 mb-8">
            Advecti operates exclusively in the Columbus metropolitan area, 
            providing targeted local advertising for businesses and earning opportunities for drivers.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="flex items-center justify-center gap-4 mb-4">
              <MapPin className="text-navy" size={32} />
              <span className="text-2xl font-bold text-navy">Columbus Metro Area</span>
            </div>
            <p className="text-navy/70">
              Including Downtown Columbus, Dublin, Westerville, Grove City, Hilliard, 
              Reynoldsburg, Gahanna, and surrounding communities.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-cream mb-4">
            Let's Talk
          </h2>
          <p className="text-xl text-cream mb-8">
            Whether you're ready to start or just have questions, we're here to help.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center gap-3 bg-cream text-navy px-10 py-5 rounded-full text-xl font-bold hover:bg-cream-light transition-all pulse-cta"
            data-testid="contact-cta-final"
          >
            Get Started Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Contact;
